import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Menu, MenuItem, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisH } from '@fortawesome/free-solid-svg-icons';
import { translate } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/@common/util/i18n-util';
import BaseMatManModal from '@monorepo/shared/src/components/common/Modals/BaseMatManModal';
import BaseModalTitle from '@monorepo/shared/src/components/common/Modals/BaseModalTitle';
import { useModal } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/Modal';
import { remCalc } from '@monorepo/shared/src/util/style-util';
import { Assignment } from '@monorepo/shared/src/graph/schema-generated-types-educator';
import { AppRoutes } from 'types';
import UseAssignmentStore from 'graph/state/query-hooks/use-assignments-store';
import useAssignments from 'state/hooks/use-assignments';

const tAssignment = (key) => translate('assignment', key);

type Props = {
  assignments: Assignment | undefined;
  letter: string;
  isAssigningALetter: boolean;
};

const AssignmentCardMenu: React.FC<Props> = ({ assignments, letter, isAssigningALetter }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const classes = useStyles();
  const open = Boolean(anchorEl);

  const [, { unassignAll, setLetter }] = useAssignments();
  const [, { openModal, closeModal }] = useModal();
  const [, { refetch }] = UseAssignmentStore();

  const isEditDisabled =
    !assignments || (assignments?.groups?.length === 0 && assignments?.students?.length === 0);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setLetter(letter);
  };

  const handleClose = () => {
    setAnchorEl(null);
    navigate(`/${AppRoutes.ASSIGNMENTS}`);
  };

  const handleEditAssignment = (event) => {
    event.stopPropagation();

    navigate(`/${AppRoutes.ASSIGNMENTS}/edit/${letter}`);
  };

  const handleAssignLetter = (event) => {
    event.stopPropagation();
    if (!isAssigningALetter) {
      handleUnassignModal();
      return;
    }
    navigate(`/${AppRoutes.ASSIGNMENTS}/${letter}`);
  };

  const handleReportRedirect = (event) => {
    event.stopPropagation();
    navigate(`/${AppRoutes.REPORTS}?character=${letter}`, {
      state: { backRoute: `/${AppRoutes.ASSIGNMENTS}` },
    });
  };

  const handleUnassignAll = async () => {
    setAnchorEl(null);
    closeModal();
    await unassignAll();
    await refetch();
  };

  const handleUnassignModal = () => {
    openModal({
      Title: () => <BaseModalTitle text={tAssignment('unassignLetter')} onClose={closeModal} />,
      body: (
        <BaseMatManModal
          leftButtonAction={closeModal}
          rightButtonAction={handleUnassignAll}
          headerText={tAssignment('unassignHeader')}
          headerFontSize={'24px'}
          leftButtonText={tAssignment('cancel')}
          rightButtonText={tAssignment('unassignLetter')}
          rightButtonClass={classes.redButton}
        >
          <Typography variant="subtitle1">
            {tAssignment('saveChangesBody1')}
            {tAssignment('saveChangesBody2')}
          </Typography>
        </BaseMatManModal>
      ),
    });
  };

  return (
    <div>
      <Button
        id={letter}
        aria-controls="basic-menu"
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
      >
        <FontAwesomeIcon id={letter} icon={faEllipsisH} size="xl" color="black" />
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        <MenuItem onClick={handleAssignLetter}>
          <Typography className={classes.text}>
            {!isAssigningALetter ? tAssignment('unassignLetter') : tAssignment('assignLetter')}
          </Typography>
        </MenuItem>
        <MenuItem onClick={handleEditAssignment} disabled={isEditDisabled}>
          <Typography className={classes.text}>{tAssignment('editAssignment')}</Typography>
        </MenuItem>
        <MenuItem onClick={handleReportRedirect}>
          <Typography className={classes.text}>{tAssignment('viewStudentProgress')}</Typography>
        </MenuItem>
      </Menu>
    </div>
  );
};

const useStyles = makeStyles(() => ({
  text: {
    fontSize: remCalc(16),
    lineHeight: remCalc(24),
  },
  redButton: {
    backgroundColor: 'red',
  },
}));

export default AssignmentCardMenu;
